import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { generateArticleStructuredData } from 'src/lib/utils';

// components
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import AuthorCard from 'src/components/Blog/Heading/Author/Author';
import { Date as ArticleDate, UpdatedDate } from 'src/components/Date/Date';
import ShareBtns from 'src/components/ShareBtns/ShareBtns';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import { Tag } from 'src/components/DataDisplay';
import LegacyScripts from 'src/components/LegacyScripts';
import { TemplateWrapper } from 'src/templates';

// helpers
import { CmsThemes, ContentfulTemplates } from 'src/lib/constants';
import {
  ContentfulSystemMetadata,
  TemplateFieldShape,
} from 'src/lib/prop-types';

// styles
import styles from './BlogPostTemplate.module.scss';

const propTypes = {
  fields: TemplateFieldShape.isRequired,
  sys: ContentfulSystemMetadata,
};

// Display tag slugs as space-separated text while preserving the original
// casing — the CMS stores acronyms like "ai"/"api"/"ach", and lowercasing the
// tail would corrupt them (e.g. "API" -> "Api"). Visual casing is handled by
// `text-transform` in the Tag styles.
const formatTagText = (rawTag) => {
  return rawTag?.replaceAll('-', ' ');
};

const BlogPostTemplate = ({
  fields: { published, updated, ...fields },
  sys,
}) => {
  const inspectorProps = useContentfulInspectorMode({ entryId: sys?.id });
  const resolvedAuthors =
    fields?.authors?.filter((author) => {
      return author?.sys?.type === 'Entry';
    }) ?? [];
  const authorNames = resolvedAuthors
    .map((author) => {
      return author?.fields?.name;
    })
    .filter(Boolean);
  const authorCards = resolvedAuthors.map((author) => {
    return {
      entryId: author?.sys?.id,
      name: author?.fields?.name,
      image: {
        src: author?.fields?.image?.fields?.file?.url || '',
        alt: author?.fields?.image?.fields?.title || author?.fields?.name || '',
      },
      title: author?.fields?.title,
      blurb: author?.fields?.blurb,
    };
  });

  return (
    <TemplateWrapper
      meta-title={fields?.metaTitle}
      meta-description={fields?.metaDescription}
      hasFooterCta={fields?.hasFooterCta}
      openGraphImage={fields?.openGraphImage?.fields?.file?.url}
      tags={fields?.tags?.map((tag) => {
        return tag?.fields?.tag;
      })}
      authors={authorNames}
      published={published}
      updated={updated}
      className='blog'
      structuredData={generateArticleStructuredData({
        authorName: authorNames.join('; '),
        datePublished: published,
        dateModified: updated || published,
        headline: fields?.metaTitle,
        image: fields.bannerImage?.fields?.file?.url,
      })}
      cmsTheme={CmsThemes.WEB3}
    >
      <article>
        <div className={styles.navspacer} />
        <div className={styles.wrapper}>
          {/* Tags + date + headline */}
          <div className={styles.pageHeader}>
            <div className={styles.metaRow}>
              <div className={styles.dateWrapper}>
                {published && <ArticleDate date={published} />}
              </div>
              <div {...inspectorProps({ fieldId: 'tags' })}>
                {fields?.tags?.map((tag) => {
                  return (
                    <Tag
                      key={tag?.fields?.tag}
                      tag={formatTagText(tag?.fields?.tag)}
                      href={`/blog/tag/${tag?.fields?.tag}/`}
                    />
                  );
                })}
              </div>
            </div>
            <h1
              className={styles.headline}
              {...inspectorProps({ fieldId: 'metaTitle' })}
            >
              {fields?.metaTitle}
            </h1>
            <ShareBtns
              title={fields?.metaTitle}
              location={`https://plaid.com/blog/${fields?.pageSlug}/`}
              variant='inline'
              showTitle={false}
              className={styles.headerShare}
            />
            {authorCards.length > 0 && (
              <div className={styles.authors}>
                {authorCards.map((author, index) => {
                  return (
                    <AuthorCard key={author.entryId || index} {...author} />
                  );
                })}
              </div>
            )}
          </div>

          {/* Article body: centered column, row 2 */}
          <div className={styles.main}>
            <div
              className={styles.body}
              data-testid={ContentfulTemplates.BLOG_POST}
            >
              <CmsComponentSwitch
                templatePage={ContentfulTemplates.BLOG_POST}
                components={fields?.components}
              />
            </div>
            {updated && updated > published && <UpdatedDate date={updated} />}
          </div>
        </div>
      </article>
      <LegacyScripts />
      <ContentfulToolbar />
    </TemplateWrapper>
  );
};
BlogPostTemplate.propTypes = propTypes;

export default BlogPostTemplate;
